import { useState, useEffect, useCallback, useRef } from 'preact/hooks';

interface UseAutoRefreshReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Auto-refreshing data fetcher.
 * Fetches immediately, then refetches on the given interval.
 * Cleans up interval on unmount.
 */
export function useAutoRefresh<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
): UseAutoRefreshReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mountedRef = useRef(true);

  const doFetch = useCallback(async (isInitial: boolean) => {
    if (isInitial) setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (mountedRef.current) {
        setData(result);
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'Fetch failed');
      }
    } finally {
      if (mountedRef.current && isInitial) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    doFetch(true);
    const id = setInterval(() => doFetch(false), intervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [intervalMs, doFetch]);

  const refresh = useCallback(() => {
    doFetch(true);
  }, [doFetch]);

  return { data, loading, error, refresh };
}
